import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPortfolios,
  createPortfolio,
  updatePortfolio,
  deletePortfolio,
} from "./index";

import useAuth from "@/hooks/useAuth";
import { useMemo, useSyncExternalStore } from "react";
import {
  applyPrebuiltOverrides,
  subscribeToPrebuiltOverrides,
  getPrebuiltOverridesSnapshot,
} from "../prebuiltWatchlists";
import {
  subscribeToDemoWatchlist,
  getDemoWatchlistSnapshot,
} from "../demoWatchlist";

export const usePortfoliosCRUD = () => {
  const queryClient = useQueryClient();
  const { authenticated } = useAuth();

  // Fetch portfolios
  const portfoliosQuery = useQuery({
    queryKey: ["get-portfolios"],
    queryFn: getPortfolios,
    enabled: authenticated,
  });

  // Create portfolio
  const createMutation = useMutation({
    mutationFn: createPortfolio,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
    },
  });

  // Update portfolio
  const updateMutation = useMutation({
    mutationFn: updatePortfolio,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
    },
  });

  // Delete portfolio
  const deleteMutation = useMutation({
    mutationFn: deletePortfolio,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
    },
  });

  // Subscribe to local prebuilt-watchlist overrides so edits in the Builder
  // re-render every consumer without needing to invalidate the query.
  const overrides = useSyncExternalStore(
    subscribeToPrebuiltOverrides,
    getPrebuiltOverridesSnapshot,
    getPrebuiltOverridesSnapshot
  );

  /**
   * The guided demo's watchlist for the asset class in play, if any.
   *
   * Logged-out visitors get 403 from /portfolios, so without this every filter
   * and highlight panel offers no lists at all — see ../demoWatchlist.
   */
  const demoWatchlist = useSyncExternalStore(
    subscribeToDemoWatchlist,
    getDemoWatchlistSnapshot,
    getDemoWatchlistSnapshot
  );

  const portfolios = useMemo(
    () => [
      ...(demoWatchlist ? [demoWatchlist] : []),
      ...applyPrebuiltOverrides(portfoliosQuery.data || []),
    ],
    [portfoliosQuery.data, overrides, demoWatchlist]
  );

  return {
    portfolios,
    portfoliosQuery,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
